import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Flex, Modal, Pagination, Select, SelectProps, Typography } from 'antd';
import { debounce } from 'lodash';
import { interpolateString } from '@utils/string';
import { useAppDispatch } from '@hooks/redux';
import useEffectUpdateOnly from '@hooks/useEffectUpdateOnly';
import { CANCEL_BUTTON_LABEL, DONE_BUTTON_LABEL, SAVE_BUTTON_LABEL } from '@constants/common';
import { StatusCodes } from '@constants/enum/common';
import {
  WORKSPACE_ACCESS_MODAL_AUTO_COMPLETE_PLACEHOLDER,
  WORKSPACE_ACCESS_MODAL_EXISTING_USERS,
  WORKSPACE_ACCESS_MODAL_TITLE,
} from '@constants/settings-page';
import { fetchNonWorkspaceUsers, fetchWorkspaceUsers } from '@redux/actions/organizationActions';
import { assignUserWorkspaceRole, removeUserFromWorkspace } from '@redux/actions/userActions';
import { IWorkspaceItem } from '@redux/reducers/organizationReducer';
import {
  fetchNonWorkspaceUsersStatusSelector,
  nonWorkspaceUsersSelector,
  workspaceUsersSelector,
} from '@redux/selectors/organizationSelectors';
import {
  assignUserToWorkspaceStatusSelector,
  removeUserFromWorkspaceStatusSelector,
} from '@redux/selectors/userSelectors';
import { IUserData } from '@redux/types/types';
import InfiniteScrollSelect from '@components/common/InfiniteScrollSelect/InfiniteScrollSelect';
import useRoles from '@modules/Roles/hooks/useRoles';
import { AssignedRole, RBACRole } from '@modules/Roles/types/roles';
import WorkspaceMember from './WorkspaceMember';
import styles from './manageWorkspaceAccessModal.module.scss';

interface IManageWorkspaceAccessProps {
  onClose: () => void;
  workspace: IWorkspaceItem;
}

const USERS_PER_PAGE = 5;
const NON_WORKSPACE_USERS_PER_PAGE = 5;
const SEARCH_DEBOUNCE_DELAY = 750;

const ManageWorkspaceAccess: React.FC<IManageWorkspaceAccessProps> = ({ onClose, workspace }) => {
  const dispatch = useAppDispatch();
  const { roles: availableRoles, fetchRoles } = useRoles();

  const { data: workspaceUsers, total: workspaceUsersTotal } = useSelector(workspaceUsersSelector);
  const { data: nonWorkspaceUsers, has_more: hasMoreNonWorkspaceUsers } =
    useSelector(nonWorkspaceUsersSelector);
  const fetchNonWorkspaceUsersStatus = useSelector(fetchNonWorkspaceUsersStatusSelector);

  const assignUserToWorkspaceStatus = useSelector(assignUserToWorkspaceStatusSelector);
  const removeUserFromWorkspaceStatus = useSelector(removeUserFromWorkspaceStatusSelector);

  const [userToSetInWorkspace, setUserToSetInWorkspace] = useState<AssignedRole | null>(null);
  const [userToRemoveFromWorkspace, setUserToRemoveFromWorkspace] = useState<string | null>(null);
  const [currentWorkspaceUsersPage, setCurrentWorkspaceUsersPage] = useState<number>(1);
  const [currentNonWorkspaceUsersPage, setCurrentNonWorkspaceUsersPage] = useState<number>(1);

  const isAssigningUser = assignUserToWorkspaceStatus === StatusCodes.IN_PROGRESS;
  const isRemovingUser = (userId: string) =>
    removeUserFromWorkspaceStatus[userId] === StatusCodes.IN_PROGRESS;

  const selectableOptions = useMemo(
    () => nonWorkspaceUsers.map((user) => ({ value: user.userID, label: user.fullName })),
    [nonWorkspaceUsers],
  );

  const getNonWorkspaceUsers = ({
    page,
    fetchMore,
    searchValue,
  }: { page?: number; fetchMore?: boolean; searchValue?: string } = {}) => {
    dispatch(
      fetchNonWorkspaceUsers({
        workspaceName: workspace.name,
        limit: NON_WORKSPACE_USERS_PER_PAGE,
        page,
        fetchMore,
        searchValue,
      }),
    );
  };

  const getAllUsers = useCallback(() => {
    dispatch(fetchWorkspaceUsers({ workspaceName: workspace.name, limit: USERS_PER_PAGE }));
    getNonWorkspaceUsers();
  }, [workspace]);

  const getNextNonWorkspaceUsers = () => {
    const nextPage = currentNonWorkspaceUsersPage + 1;
    getNonWorkspaceUsers({ page: nextPage, fetchMore: true });
    setCurrentNonWorkspaceUsersPage(nextPage);
  };

  const debouncedSearchNonWorkspaceUsers = useMemo(
    () =>
      debounce(
        (value) => getNonWorkspaceUsers({ searchValue: value, page: 1 }),
        SEARCH_DEBOUNCE_DELAY,
      ),
    [],
  );

  const userRolesOptions: SelectProps['options'] = availableRoles.map(
    ({ role_name: label, role_id: value }: RBACRole) => ({ label, value }),
  );

  const getWorkspaceUsers = () => {
    dispatch(
      fetchWorkspaceUsers({
        workspaceName: workspace.name,
        page: currentWorkspaceUsersPage,
        limit: USERS_PER_PAGE,
      }),
    );
  };

  useEffect(() => {
    getAllUsers();
    fetchRoles();
  }, []);

  useEffectUpdateOnly(() => {
    getWorkspaceUsers();
  }, [currentWorkspaceUsersPage]);

  useEffect(() => {
    if (assignUserToWorkspaceStatus === StatusCodes.SUCCESS) {
      getAllUsers();
      setUserToSetInWorkspace(null);
    }
  }, [assignUserToWorkspaceStatus]);

  useEffect(() => {
    if (!userToRemoveFromWorkspace) return;

    const userRemovedStatus = removeUserFromWorkspaceStatus[userToRemoveFromWorkspace];

    if (userRemovedStatus === StatusCodes.SUCCESS) {
      getAllUsers();
      setUserToRemoveFromWorkspace(null);
    }

    if (userRemovedStatus === StatusCodes.ERROR) {
      setUserToRemoveFromWorkspace(null);
    }
  }, [removeUserFromWorkspaceStatus]);

  const getUserWorkspaceRole = (user: IUserData) => {
    return user.workspaces.find((ws) => ws.name === workspace.name)?.role;
  };

  const resetModal = () => {
    setCurrentWorkspaceUsersPage(1);
    setCurrentNonWorkspaceUsersPage(1);
    setUserToSetInWorkspace(null);
    setUserToRemoveFromWorkspace(null);
  };

  const onUserSelected = useCallback(
    (userId: string) => {
      const user = nonWorkspaceUsers.find((u) => u.userID === userId);
      if (!user) return;
      setUserToSetInWorkspace({ user, roleId: '' });
    },
    [nonWorkspaceUsers],
  );

  const onUserSelectedRoleChange = (roleId: string) => {
    if (!userToSetInWorkspace || !roleId) {
      return;
    }

    setUserToSetInWorkspace({ ...userToSetInWorkspace, roleId });
  };

  const onRoleChange = ({ user, roleId }: AssignedRole) => {
    const { role_name: roleName } = availableRoles.find((r) => r.role_id === roleId) || {};
    if (!roleName || getUserWorkspaceRole(user) === roleName) {
      return;
    }

    dispatch(
      assignUserWorkspaceRole({
        userId: user.userID,
        workspaceName: workspace.name,
        roleName,
      }),
    );
  };

  const onRemove = (user: IUserData) => {
    setUserToRemoveFromWorkspace(user.userID);
    dispatch(removeUserFromWorkspace({ userId: user.userID, workspaceName: workspace.name }));
  };

  // Renders
  const renderNonWorkspaceUsersSelect = ({ value }: { value?: string } = {}) => {
    return (
      <InfiniteScrollSelect
        className={styles.searchInput}
        options={selectableOptions}
        onSelect={onUserSelected}
        onSearch={debouncedSearchNonWorkspaceUsers}
        showSearch
        value={value}
        loading={fetchNonWorkspaceUsersStatus === StatusCodes.IN_PROGRESS}
        hasMore={hasMoreNonWorkspaceUsers}
        placeholder={WORKSPACE_ACCESS_MODAL_AUTO_COMPLETE_PLACEHOLDER}
        totalLoaded={nonWorkspaceUsers.length}
        loadMore={getNextNonWorkspaceUsers}
      />
    );
  };

  const renderAddUserView = () => {
    return (
      <Flex gap="small" className={styles.section}>
        {renderNonWorkspaceUsersSelect({ value: userToSetInWorkspace?.user?.fullName })}
        <Select
          className={styles.select}
          options={userRolesOptions}
          onChange={onUserSelectedRoleChange}
        />
      </Flex>
    );
  };

  const renderListView = () => {
    return (
      <>
        {renderNonWorkspaceUsersSelect()}
        {!!workspaceUsers?.length && (
          <Flex gap="middle" vertical className={styles.section} flex={1}>
            <Typography.Title level={5}>
              {interpolateString(WORKSPACE_ACCESS_MODAL_EXISTING_USERS, {
                count: workspaceUsersTotal,
              })}
            </Typography.Title>
            {workspaceUsers.map((user) => (
              <WorkspaceMember
                key={user.userID}
                user={user}
                workspaceName={workspace.name}
                onRemove={onRemove}
                onRoleChange={onRoleChange}
                roleOptions={userRolesOptions}
                isDisabled={isAssigningUser || isRemovingUser(user.userID)}
              />
            ))}
          </Flex>
        )}
        {workspaceUsersTotal > USERS_PER_PAGE && (
          <Pagination
            current={currentWorkspaceUsersPage}
            total={workspaceUsersTotal}
            defaultPageSize={USERS_PER_PAGE}
            showSizeChanger={false}
            size="small"
            onChange={(page) => setCurrentWorkspaceUsersPage(page)}
          />
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (!userToSetInWorkspace) {
      return (
        <Flex justify="flex-end" gap="middle">
          <Button disabled={isRemovingUser(userToRemoveFromWorkspace || '')} onClick={onClose}>
            {CANCEL_BUTTON_LABEL}
          </Button>
          <Button
            disabled={isRemovingUser(userToRemoveFromWorkspace || '')}
            type="primary"
            onClick={onClose}
          >
            {DONE_BUTTON_LABEL}
          </Button>
        </Flex>
      );
    }

    return (
      <Flex justify="flex-end" gap="middle">
        <Button disabled={isAssigningUser} onClick={() => setUserToSetInWorkspace(null)}>
          {CANCEL_BUTTON_LABEL}
        </Button>
        <Button
          disabled={isAssigningUser}
          loading={isAssigningUser}
          type="primary"
          onClick={() => onRoleChange(userToSetInWorkspace)}
        >
          {SAVE_BUTTON_LABEL}
        </Button>
      </Flex>
    );
  };

  if (!workspace) return null;

  return (
    <Modal
      data-testid="workspacesSettings_manageAccess_modal"
      title={interpolateString(WORKSPACE_ACCESS_MODAL_TITLE, { workspaceName: workspace.name })}
      open={true}
      onCancel={onClose}
      afterClose={resetModal}
      className={styles.modal}
      footer={renderFooter()}
    >
      <Flex gap="large" vertical className={styles.scrollWrapper}>
        {!!userToSetInWorkspace ? renderAddUserView() : renderListView()}
      </Flex>
    </Modal>
  );
};

export default ManageWorkspaceAccess;
